import { createContext, ReactNode, useCallback, useMemo } from 'react';

// project-imports
import config, { MenuOrientation, ThemeDirection, ThemeMode } from 'config';
import useLocalStorage from 'hooks/useLocalStorage';

// types
import { CustomizationProps, DefaultConfigProps, FontFamily, I18n, PresetColor } from 'types/config';

// initial state
const initialState: CustomizationProps = {
  ...config,
  onChangeContainer: () => {},
  onChangeLocalization: () => {},
  onChangeMode: () => {},
  onChangePresetColor: () => {},
  onChangeDirection: () => {},
  onChangeMiniDrawer: () => {},
  onChangeMenuOrientation: () => {},
  onChangeMenuCaption: () => {},
  onChangeFontFamily: () => {},
  onChangeContrast: () => {}
};

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactNode;
};

function ConfigProvider({ children }: Readonly<ConfigProviderProps>) {
  const [config, setConfig] = useLocalStorage('barChannel-admin-config', initialState);

  const onChangeContainer = useCallback(
    (container: string) => {
      setConfig((prev: DefaultConfigProps) => ({
        ...prev,
        container: container !== 'fluid'
      }));
    },
    [setConfig]
  );

  const onChangeLocalization = useCallback(
    (lang: I18n) => {
      setConfig((prev: DefaultConfigProps) => ({ ...prev, i18n: lang }));
    },
    [setConfig]
  );

  const onChangeMode = useCallback(
    (mode: ThemeMode) => {
      setConfig((prev: DefaultConfigProps) => ({ ...prev, mode }));
    },
    [setConfig]
  );

  const onChangePresetColor = useCallback(
    (theme: PresetColor) => {
      setConfig((prev: DefaultConfigProps) => ({ ...prev, presetColor: theme }));
    },
    [setConfig]
  );

  const onChangeDirection = useCallback(
    (direction: ThemeDirection) => {
      setConfig((prev: DefaultConfigProps) => ({ ...prev, themeDirection: direction }));
    },
    [setConfig]
  );

  const onChangeMiniDrawer = useCallback(
    (miniDrawer: boolean) => {
      setConfig((prev: DefaultConfigProps) => ({ ...prev, menuOrientation: MenuOrientation.VERTICAL, miniDrawer }));
    },
    [setConfig]
  );

  const onChangeMenuOrientation = useCallback(
    (layout: MenuOrientation) => {
      setConfig((prev: DefaultConfigProps) => ({
        ...prev,
        menuOrientation: layout,
        ...(layout === MenuOrientation.VERTICAL && { miniDrawer: false })
      }));
    },
    [setConfig]
  );

  const onChangeContrast = useCallback(
    (themeContrast: string) => {
      setConfig((prev: DefaultConfigProps) => ({ ...prev, themeContrast: themeContrast === 'contrast' }));
    },
    [setConfig]
  );

  const onChangeMenuCaption = useCallback(
    (menuCaption: string) => {
      setConfig((prev: DefaultConfigProps) => ({ ...prev, menuCaption: menuCaption === 'caption' }));
    },
    [setConfig]
  );

  const onChangeFontFamily = useCallback(
    (fontFamily: FontFamily) => {
      setConfig((prev: DefaultConfigProps) => ({ ...prev, fontFamily }));
    },
    [setConfig]
  );

  const contextValue = useMemo(
    () => ({
      ...config,
      onChangeContainer,
      onChangeLocalization,
      onChangeMode,
      onChangePresetColor,
      onChangeDirection,
      onChangeMiniDrawer,
      onChangeMenuOrientation,
      onChangeMenuCaption,
      onChangeFontFamily,
      onChangeContrast,
    }),
    [
      config,
      onChangeContainer,
      onChangeLocalization,
      onChangeMode,
      onChangePresetColor,
      onChangeDirection,
      onChangeMiniDrawer,
      onChangeMenuOrientation,
      onChangeMenuCaption,
      onChangeFontFamily,
      onChangeContrast,
    ]
  );

  return <ConfigContext.Provider value={contextValue}>{children}</ConfigContext.Provider>;
}

export { ConfigProvider, ConfigContext };
